import { ActionTree } from 'vuex';
import { EFieldAction, EFieldMutation } from './field.constants';
import { IFieldState } from './field.types';
import { IFieldPosition } from '@lib-shared/fields/types/field-position';
import { IField } from '@lib-shared/fields/types/field';
import { FieldPlacementHelper } from '@/document-edit/assembler/fields/helpers/field-placement.helper';
import { FieldService } from '@/api/fields/services/field.service';
import { EAlignFieldType } from '@/document-edit/assembler/fields/context-menu/types/enums/align-field-type';
import { FieldHelper } from '@/document-edit/assembler/fields/helpers/field.helper';
import { EFieldType } from '@lib-shared/fields/types/enums/field-type';
import { MergeTagHelper } from '@/helpers/merge-tag.helper';
import { translate } from '@/helpers/translation.helper';
import { RootState } from '@/store/store';
import { alertNamespace } from '@/alert/store/alert.constants';
import { IDocumentPage } from '@lib-shared/types/document-page';

export const actions: ActionTree<IFieldState, RootState> = {
  async [EFieldAction.Load]({ dispatch, commit }, documentId: string) {
    commit(EFieldMutation.SetDocumentId, documentId);
    commit(EFieldMutation.SetLoading, true);
    try {
      const fields: IField[] = await FieldService.load(documentId);
      const mappedFields: IField[] = fields.map((field: IField) =>
        field.type === EFieldType.Merge
          ? {
              ...field,
              displayProperty: MergeTagHelper.getMergeDisplayProperty(field.value),
            }
          : field
      );
      dispatch(EFieldAction.LoadSuccess, mappedFields);
    } catch (err) {
      dispatch(EFieldAction.LoadFailed);
    }
  },
  [EFieldAction.LoadSuccess]({ commit }, fields: IField[]) {
    commit(EFieldMutation.Set, fields);
    commit(EFieldMutation.SetLoading, false);
  },
  [EFieldAction.LoadFailed]({ commit }) {
    commit(EFieldMutation.SetLoading, false);
  },
  [EFieldAction.Add]({ commit }, fields: IField[]) {
    commit(EFieldMutation.Add, fields);
  },
  [EFieldAction.Remove]({ commit }, fields: IField[]) {
    commit(EFieldMutation.Remove, fields);
  },
  [EFieldAction.ReplaceCreatedFields](
    { commit, state },
    fieldsWithId: { tempId: string; field: IField }[]
  ) {
    const fieldsWithCorrectId: IField[] = state.fields.map(
      (field: IField) =>
        fieldsWithId.find(
          (fieldWithId: { tempId: string; field: IField }) =>
            fieldWithId.tempId === field.id && fieldWithId.field.pageId === field.pageId
        )?.field || field
    );
    commit(EFieldMutation.Set, fieldsWithCorrectId);
  },
  async [EFieldAction.Update]({ commit, state, dispatch }, fields: IField[]) {
    const previousFields: IField[] = state.fields;
    const updatedFields: IField[] = previousFields.map((stateField: IField) =>
      fields.some((field: IField) => field.id === stateField.id)
        ? fields.find((field: IField) => field.id === stateField.id)
        : stateField
    );
    dispatch(EFieldAction.SetLastUpdatedFieldId, fields);
    commit(EFieldMutation.Set, updatedFields);
    try {
      await FieldService.updateMultiple(fields);
    } catch {
      dispatch(EFieldAction.UpdateFailed, previousFields);
    }
  },
  [EFieldAction.UpdateFailed]({ commit, dispatch }, previousField: IField) {
    commit(EFieldMutation.Update, previousField);
    dispatch(`${alertNamespace}/showDanger`, translate('could-not-update-field'), {
      root: true,
    });
  },
  [EFieldAction.SetTabStart]({ commit }, field: IField) {
    commit(EFieldMutation.SetTabStartField, field);
  },
  [EFieldAction.Tab]({ commit, state }, pages: IDocumentPage[]) {
    const field: IField = FieldHelper.getNextTabField(state.fields, pages, state.tabStartField);
    commit(EFieldMutation.SetTabbedField, field);
  },
  [EFieldAction.UpdatePageWidth]({ commit }, pageWidth: number) {
    commit(EFieldMutation.SetPageWidth, pageWidth);
  },
  [EFieldAction.Align](
    { dispatch },
    { actionType, fields }: { actionType: EAlignFieldType; fields: IField[] }
  ) {
    const fieldContainerPosition: IFieldPosition = FieldPlacementHelper.calculateFieldContainerPosition(
      fields
    );
    const updatedFields: IField[] = FieldHelper.getAlignedFields(
      fields,
      actionType,
      fieldContainerPosition
    );
    dispatch(EFieldAction.Update, updatedFields);
  },
  [EFieldAction.Move](
    { dispatch, state },
    { pageId, fields }: { pageId: string; fields: IField[] }
  ) {
    const movedFields: IField[] = fields.map((field: IField) => ({
      ...field,
      pageId,
      position: FieldPlacementHelper.preventFieldPositionBeingOutsidePage(
        field.position,
        pageId,
        state.pageWidth
      ),
    }));

    dispatch(EFieldAction.Update, movedFields);
  },
  [EFieldAction.ChangeTextSize](
    { dispatch },
    { size, fields }: { size: number; fields: IField[] }
  ) {
    const updatedFields: IField[] = fields.map((selectedField: IField) => {
      if (selectedField.position.height !== size && selectedField.type !== EFieldType.Check) {
        selectedField.position.height = size;
      }
      return selectedField;
    });
    dispatch(EFieldAction.Update, updatedFields);
  },
  [EFieldAction.Focus]({ commit }, fieldId: string) {
    commit(EFieldMutation.SetFocusedFieldId, fieldId);
  },
  async [EFieldAction.LoadSenderSignature]({ dispatch }, userId: string) {
    try {
      const signatureData: string = await FieldService.loadUserSignature(userId);
      dispatch(EFieldAction.LoadSenderSignatureSuccess, signatureData);
    } catch (e) {
      dispatch(EFieldAction.LoadSenderSignatureFailed);
    }
  },
  [EFieldAction.LoadSenderSignatureSuccess]({ commit }, signatureData: string) {
    commit(EFieldMutation.SetSenderSignature, signatureData);
  },
  [EFieldAction.LoadSenderSignatureFailed]({ dispatch }) {
    dispatch(`${alertNamespace}/showDanger`, translate('could-not-load-signature'), {
      root: true,
    });
  },
  [EFieldAction.SetLastUpdatedFieldId]({ commit }, updatedFields: IField[]) {
    const sortedUpdatedFields: IField[] = FieldHelper.sortByPositionWithoutPages(updatedFields);
    const lastUpdatedField: IField = sortedUpdatedFields[sortedUpdatedFields.length - 1];
    commit(EFieldMutation.SetLastUpdatedFieldId, lastUpdatedField.id);
  },
  [EFieldAction.DragField]({ commit }, isDragging: boolean) {
    commit(EFieldMutation.SetDragField, isDragging);
  },
};
